import React, { useEffect, useState } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../config';
import { useTranslation } from 'react-i18next';
import { getAdminBadge } from '../utils/adminBadge';
import 'react-calendar-heatmap/dist/styles.css';

export default function HeatmapModal({ onClose, filters, fetchWithAuth }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState('');

    // Add escape key handler
    useEffect(() => {
      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, []);

  useEffect(() => {
    const fetchHeatmapData = async () => {
      const params = new URLSearchParams();

      console.log('filters', filters, params);

      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'createdat' && value) {
          params.append('createdat[startDate]', value.startDate);
          params.append('createdat[endDate]', value.endDate);
        } else if (key === 'favorite' || key === 'latest_visit') {
          if (value === true) {
            params.append(key, 'true');
          }
        } else if (key === 'project') {
          for (const project of value) {
            params.append('project', project?.value);
          }
        } else if (value?.value) {
          params.append(key, value.value);
        }
      });

      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/heatmap?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        const heatmapData = await response.json();

        const values = Object.entries(heatmapData).map(([date, count]) => ({
          date,
          count
        }));

        setData(values);
      } catch (error) {
        console.error('Error fetching heatmap data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeatmapData();
  }, [filters, fetchWithAuth]);

  let startDate = dayjs().startOf('year');
  let endDate = dayjs().endOf('year');

  if (filters.createdat) {
    startDate = dayjs(filters.createdat.startDate);
    endDate = dayjs(filters.createdat.endDate);
  }

  // if range is less than 1 year, set startDate to 1 year before endDate
  if (endDate.diff(startDate, 'day') < 365) {
    startDate = endDate.subtract(1, 'year');
  }

  startDate = startDate.toDate();
  endDate = endDate.toDate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg p-6 max-w-4xl w-[90%] mx-4 md:h-auto overflow-y-auto"
        style={{ maxHeight: 'calc(100svh - 2rem)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-4 z-10">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            {getAdminBadge()}
            {t('Frequency of photos')}
          </h2>
          <button className="text-gray-400 hover:text-gray-500" onClick={onClose}>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="animate-pulse h-32 bg-gray-100 rounded"></div>
        ) : (
          <div className="flex flex-col">
            <div className="flex-grow">
              <div className="overflow-hidden">
                <CalendarHeatmap
                  gutterSize={1}
                  startDate={startDate}
                  endDate={endDate}
                  values={data}
                  horizontal={window.innerWidth >= 768}
                  classForValue={(value) => {
                    if (!value || value.count === 0) return 'color-empty';
                    if (value.count <= 2) return 'color-scale-1';
                    if (value.count <= 5) return 'color-scale-2';
                    if (value.count <= 10) return 'color-scale-3';
                    return 'color-scale-4';
                  }}
                  onMouseOver={(event, value) => {
                    setDetails(!value ? t('No photos') : t('photoOnDate', { count: value.count, date: dayjs(value.date).format('DD MMMM YYYY') }));
                  }}
                  showWeekdayLabels={true}
                  weekdayLabels={[t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')]}
                />
              </div>
            </div>

            <div className="flex items-center justify-start gap-2 text-sm text-gray-500">
              <span>{details}</span>
            </div>

            <div className="flex items-center justify-end gap-2 text-sm text-gray-500 sticky bottom-0 bg-white pt-4 z-10">
              <span>{t('Less')}</span>
              <div className="flex gap-1">
                <div className="w-3 h-3 bg-[#ebedf0]"></div>
                <div className="w-3 h-3 bg-[#9be9a8]"></div>
                <div className="w-3 h-3 bg-[#40c463]"></div>
                <div className="w-3 h-3 bg-[#30a14e]"></div>
                <div className="w-3 h-3 bg-[#216e39]"></div>
              </div>
              <span>{t('More')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}